'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/getting-started/my-corner.mdx
 * (published at user-docs.totalaccess.co.za/getting-started/my-corner#features-and-bugs)
 */

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Bug, Sparkles, X, Send, Paperclip, Trash2, Upload, FileText, Clock, Search, ArrowLeft, LifeBuoy, Ticket, Inbox } from 'lucide-react';
import { authService, type User } from '@/lib/authService';
import { isAppShellRoute } from '@/lib/platformUtils';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigation } from '@/hooks/useNavigation';
import BrandedLoadingSpinner, { SectionLoader } from './shared/BrandedLoadingSpinner';
import ModalFooter from './shared/ModalFooter';
import MyTicketsView from '@/app/client-zone/modules/total-support/components/support/MyTicketsView';
import SupportQueueView from '@/app/client-zone/modules/total-support/components/support/SupportQueueView';
import SupportTicketDetailModal from '@/components/SupportTicketDetailModal';
import { emitGuidedTourTrainingEvent } from '@/components/training/guided-tour';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { STATIC_FILE_SERVER_UPLOAD_URL as STATIC_FILE_SERVER, STATIC_FILE_SERVER_API_KEY as UPLOAD_API_KEY } from '@/lib/staticFileServerUrl';

/** Above sticky header (`z-[80]`) and shell overlays (~`z-[120]`); full-screen blocker. */
const SUPPORT_MODAL_OVERLAY_Z_CLASS = 'z-[230]';

/** FAB trigger sits above sticky header chrome. */
const SUPPORT_MODAL_TRIGGER_FAB_Z_CLASS = 'z-[96]';

type SupportModalView = 'bug' | 'feature' | 'my-reports' | 'ticket' | 'my-tickets' | 'queue';

type ActiveClientSummary = { id: number; name: string };

interface Attachment {
  id: string;
  filename: string;
  originalFilename: string;
  url: string;
  contentType: string;
  size: number;
  isImage: boolean;
}

interface UserReport {
  id: string;
  item_number: string;
  item_type: 'bug' | 'feature';
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  reporter_name?: string | null;
  reporter_email?: string | null;
  created_at: string;
  updated_at: string;
}

export type SupportModalHandle = {
  /** Open the modal. Safe to call while header menus are closing — host instance stays mounted in `Header`. */
  open: () => void;
  /** Close the modal. Used by the guided tour to dismiss the revealed bug-report modal before advancing. */
  close: () => void;
};

interface SupportModalProps {
  className?: string;
  variant?: 'dropdown' | 'mobile' | 'fab' | 'portal-only';
  defaultType?: SupportModalView;
  onClose?: () => void;
}

const SupportModal = forwardRef<SupportModalHandle, SupportModalProps>(function SupportModal({
  className = '',
  variant = 'dropdown',
  onClose,
}: SupportModalProps, ref) {
  const { clientBranding } = useTheme();
  const { canAccessModule } = useNavigation();
  // true when the active client has total-support — unlocks the rep queue tab
  const hasModuleAccess = canAccessModule('total-support');
  const [showModal, setShowModal] = useState(false);
  const [reportType, setReportType] = useState<SupportModalView | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical' | 'urgent'>('medium');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState<WorkItem | null>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [client, setClient] = useState<ActiveClientSummary | null>(null);
  const [mounted, setMounted] = useState(false);
  const [userReports, setUserReports] = useState<UserReport[]>([]);
  const [reportSearch, setReportSearch] = useState('');
  const [selectedReport, setSelectedReport] = useState<UserReport | null>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [updatingReportStatus, setUpdatingReportStatus] = useState(false);
  const [lastSubmittedRef, setLastSubmittedRef] = useState<string | null>(null);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => {
      emitGuidedTourTrainingEvent({ type: 'support_modal_opened' });
      setShowModal(true);
    },
    close: () => {
      setShowModal(false);
      setSubmitSuccess(false);
      setSelectedReport(null);
    },
  }), []);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const userData = authService.getUser();
    setUser(userData);

    const loadClientInfo = async () => {
      try {
        // Portal-only button — skip the authed call on public pages where a
        // stale session would just 401.
        if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) return;
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        const response = await fetch('/api/v1/clients', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.active_client) {
            setClient({
              id: data.active_client.id,
              name: data.active_client.trading_name || data.active_client.name || 'Your Organization'
            });
          }
        }
      } catch (err) {
        console.error('[SupportModal] Failed to load client info:', err);
      }
    };

    loadClientInfo();
  }, []);

  const fetchUserReports = useCallback(async () => {
    try {
      setLoadingReports(true);
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const params = new URLSearchParams({
        my_reports: 'true',
        limit: '50',
      });

      if (user?.id) {
        params.set('reported_by_user_id', String(user.id));
      }

      const response = await fetch(`/api/v1/platform-feedback?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.reports)) {
          const ownReports = (data.reports as UserReport[]).filter((report) => {
            if (report.item_type !== 'bug' && report.item_type !== 'feature') {
              return false;
            }

            return true;
          });

          setUserReports(ownReports);

          setSelectedReport((previous) => {
            if (!previous) {
              return previous;
            }

            return ownReports.find((report) => report.id === previous.id) || null;
          });
        }
      }
    } catch (err) {
      console.error('[SupportModal] Failed to load user reports:', err);
    } finally {
      setLoadingReports(false);
    }
  }, [user?.id]);

  // Fetch user's own reports
  useEffect(() => {
    if (showModal) {
      void fetchUserReports();
    }
  }, [fetchUserReports, showModal]);

  // Only reset form fields when modal closes, keep reportType for return behavior
  useEffect(() => {
    if (!showModal) {
      setTimeout(() => {
        setReportType(null);
        setTitle('');
        setDescription('');
        setPriority('medium');
        setSubmitSuccess(false);
        setError(null);
        setAttachments([]);
        setSelectedReport(null);
      }, 300);
    }
  }, [showModal]);

  const uploadAttachment = async (file: File): Promise<Attachment | null> => {
    try {
      setUploadingAttachment(true);
      setError(null);

      if (file.size > 10 * 1024 * 1024) {
        setError(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return null;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(STATIC_FILE_SERVER, {
        method: 'POST',
        headers: { 'x-api-key': UPLOAD_API_KEY },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Upload failed');

      const isImage = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(data.originalFilename);

      return {
        id: data.token,
        filename: data.filename,
        originalFilename: data.originalFilename,
        url: data.directUrl,
        contentType: data.contentType,
        size: data.size,
        isImage
      };
    } catch (error) {
      console.error('[QuickBugReport] Upload error:', error);
      setError('Failed to upload file. Please try again.');
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      const attachment = await uploadAttachment(file);
      if (attachment) setAttachments(prev => [...prev, attachment]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        const timestamp = Date.now();
        const ext = item.type.split('/')[1] || 'png';
        const namedFile = new File([file], `screenshot_${timestamp}.${ext}`, { type: file.type });

        const attachment = await uploadAttachment(namedFile);
        if (attachment) setAttachments(prev => [...prev, attachment]);
      }
    }
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      const attachment = await uploadAttachment(file);
      if (attachment) setAttachments(prev => [...prev, attachment]);
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatRelativeTime = (value: string): string => {
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) {
      return 'just now';
    }

    const diffMs = Date.now() - timestamp;
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diffMs < minute) return 'just now';
    if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
    if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
    return `${Math.floor(diffMs / day)}d ago`;
  };

  const getStatusBadgeClass = (status: string): string => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'closed' || normalized === 'completed' || normalized === 'resolved') {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
    }
    if (normalized === 'cancelled') {
      return 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
    if (normalized === 'in-progress' || normalized === 'in_progress') {
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
    }
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400';
  };

  const handleOpenModal = () => {
    // The combined Help & Feedback surface also hosts support tickets — emit
    // the support event the platform-basics tour listens for.
    emitGuidedTourTrainingEvent({ type: 'support_modal_opened' });
    setShowModal(true);
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    onClose?.();
  }, [onClose]);

  const handleBack = useCallback(() => {
    if (reportType === 'my-reports' && selectedReport) {
      setSelectedReport(null);
      setError(null);
      return;
    }

    if (reportType) {
      setSubmitSuccess(false);
      setLastSubmittedRef(null);
      setError(null);
      setReportType(null);
      setTitle('');
      setDescription('');
      setPriority('medium');
      setAttachments([]);
    }
  }, [reportType, selectedReport]);

  /** Backdrop must not discard an active bug/feature/ticket form — use ✕ or Cancel. */
  const allowsBackdropDismiss =
    reportType !== 'bug' && reportType !== 'feature' && reportType !== 'ticket';

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (!allowsBackdropDismiss) return;
    handleCloseModal();
  };

  const handleUpdateReportStatus = useCallback(async (report: UserReport, nextStatus: 'closed' | 'cancelled') => {
    try {
      setUpdatingReportStatus(true);
      setError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/v1/platform-feedback', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: report.id,
          status: nextStatus,
        })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success || !data?.item) {
        throw new Error(data?.error || `Failed to mark report as ${nextStatus}`);
      }

      const updatedReport = data.item as UserReport;
      setUserReports((previous) => previous.map((item) => item.id === updatedReport.id ? updatedReport : item));
      setSelectedReport(updatedReport);
    } catch (err: unknown) {
      console.error('[SupportModal] Failed to update report status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update report status. Please try again.');
    } finally {
      setUpdatingReportStatus(false);
    }
  }, []);

  const handleSubmit = useCallback(async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const payload = {
        title: title.trim(),
        description: description.trim(),
        item_type: reportType,
        priority: priority,
        attachments: attachments.map(a => ({
          url: a.url,
          filename: a.originalFilename,
          contentType: a.contentType,
          size: a.size,
          isImage: a.isImage
        }))
      };

      const response = await fetch('/api/v1/platform-feedback', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit');
      }

      const result = await response.json();

      if (attachments.length > 0 && result.item?.id) {
        try {
          await fetch('/api/v1/work-items/attachments', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              work_item_id: result.item.id,
              attachments: attachments.map(a => ({
                file_url: a.url,
                file_name: a.originalFilename,
                file_type: a.contentType,
                file_size: a.size,
                category: a.isImage ? 'screenshot' : 'document'
              }))
            })
          });
        } catch (attachErr) {
          console.error('[QuickBugReport] Failed to save attachments:', attachErr);
        }
      }

      // Store the reference number for display
      if (result.item?.item_number) {
        setLastSubmittedRef(result.item.item_number);
      }

      setSubmitSuccess(true);
      window.dispatchEvent(new CustomEvent('platformFeedbackSubmitted', { detail: { type: reportType } }));

      // Refresh user reports list
      await fetchUserReports();

      // Don't auto-close modal, let user see success and choose to go back
    } catch (err: unknown) {
      console.error('Quick report submission error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [attachments, description, fetchUserReports, priority, reportType, title]);

  const renderButton = () => {
    if (variant === 'portal-only') {
      return null;
    }

    if (variant === 'dropdown') {
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenModal();
          }}
          className={`flex items-center w-full px-4 py-2 theme-text-primary hover:theme-surface-dark text-left transition-colors ${className}`}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--theme-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '')}
        >
          <span className="mr-3">🎧</span>
          Help &amp; Feedback
        </button>
      );
    }

    if (variant === 'mobile') {
      return (
        <button
          onClick={handleOpenModal}
          className={`flex items-center gap-3 px-4 py-3 w-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
        >
          <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
            <Bug className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 text-left">
            <div className="font-medium text-gray-900 dark:text-white">Help &amp; Feedback</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Get help, report issues, or track tickets</div>
          </div>
        </button>
      );
    }

    return (
      <button
        onClick={handleOpenModal}
        className={`fixed bottom-20 right-4 w-14 h-14 rounded-full bg-purple-500 hover:bg-purple-600 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110 ${SUPPORT_MODAL_TRIGGER_FAB_Z_CLASS} ${className}`}
        title="Help & Feedback"
      >
        <Bug className="w-6 h-6" />
      </button>
    );
  };

  // Get primary color with fallback
  const primaryColor = clientBranding?.primary_color || '#7c3aed';

  // Generate gradient colors based on primary
  const getHeaderStyle = () => {
    const primary = primaryColor;
    return {
      background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 50%, ${primary}bb 100%)`
    };
  };

  const selectedReportIsTerminal = selectedReport
    ? ['closed', 'cancelled', 'completed', 'resolved'].includes(String(selectedReport.status || '').toLowerCase())
    : false;

  // My Reports search — client-side match on title/number/type/status/
  // priority/description (same field set WorkItemsFilterBar searches).
  const rq = reportSearch.trim().toLowerCase();
  const filteredReports = rq
    ? userReports.filter((r) =>
        [r.title, r.item_number, r.item_type, r.status, r.priority, r.description]
          .some((v) => (v || '').toLowerCase().includes(rq)))
    : userReports;

  const modalContent = showModal && mounted ? (
    <div
      className={`fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto ${SUPPORT_MODAL_OVERLAY_Z_CLASS}`}
      onClick={handleBackdropClick}
      data-bug-report-modal="backdrop"
      data-tour-id="bug-report-modal"
      role="presentation"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex min-h-full flex-col px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4" onClick={(e) => e.stopPropagation()} data-bug-report-modal="true">

        {!reportType ? (
          <div data-bug-report-modal="true">
            {/* Header */}
            <div className="mb-3 sm:mb-6 px-2 relative">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
                  <LifeBuoy className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">Help &amp; Feedback</h1>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Action buttons */}
            <div className="px-2 space-y-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setReportType('ticket'); }}
                className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <LifeBuoy className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">Support Ticket</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Need help? Log a ticket with your support desk</p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setReportType('bug'); }}
                className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center flex-shrink-0">
                    <Bug className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">Report an Issue</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Found a bug? Let us know so we can fix it fast</p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setReportType('feature'); }}
                className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">Request a Feature</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Have an idea? Your suggestions shape our roadmap</p>
                  </div>
                </div>
              </button>

              {/* My Reports button */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setReportType('my-reports'); }}
                className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">
                      My Reports{userReports.length > 0 ? ` (${userReports.length})` : ''}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">View your submitted bugs and feature requests</p>
                  </div>
                </div>
              </button>

              {/* My Tickets */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setReportType('my-tickets'); }}
                className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                    <Ticket className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">My Tickets</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Track the status of your support tickets</p>
                  </div>
                </div>
              </button>

              {/* Support Queue — total-support reps only */}
              {hasModuleAccess && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); setReportType('queue'); }}
                  className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center flex-shrink-0">
                      <Inbox className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left flex-1">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white mb-0.5">Support Queue</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Work the support desk queue</p>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        ) : reportType === 'my-reports' ? (
          <div>
            <div className="relative mb-3 sm:mb-6 px-2">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
                  <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">My Reports</h1>
              </div>
              <button
                type="button"
                onClick={handleBack}
                aria-label="Back"
                title="Back"
                className="absolute left-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={handleCloseModal} className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-2">
              {loadingReports ? (
                <SectionLoader size="sm" primaryColor="#9333ea" className="py-12" />
              ) : selectedReport ? (
                <div className="space-y-4">
                  <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="mb-1 text-xs font-mono text-gray-500 dark:text-gray-400">{selectedReport.item_number}</div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selectedReport.title}</h3>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${getStatusBadgeClass(selectedReport.status)}`}>
                          {selectedReport.status?.replace('_', ' ')}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${selectedReport.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                          selectedReport.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                            selectedReport.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                              'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          }`}>
                          {selectedReport.priority || 'medium'}
                        </span>
                      </div>
                    </div>

                    <dl className="mt-4 grid grid-cols-1 gap-3 text-sm text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Type</dt>
                        <dd className="mt-1 capitalize">{selectedReport.item_type}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Submitted</dt>
                        <dd className="mt-1">
                          {new Date(selectedReport.created_at).toLocaleString()} · {formatRelativeTime(selectedReport.created_at)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Last updated</dt>
                        <dd className="mt-1">
                          {new Date(selectedReport.updated_at).toLocaleString()} · {formatRelativeTime(selectedReport.updated_at)}
                        </dd>
                      </div>
                      {(selectedReport.reporter_name || selectedReport.reporter_email) && (
                        <div>
                          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Reporter</dt>
                          <dd className="mt-1">{selectedReport.reporter_name || selectedReport.reporter_email}</dd>
                        </div>
                      )}
                    </dl>

                    <div className="mt-4">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Description</div>
                      <div className="mt-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-200 whitespace-pre-wrap">
                        {selectedReport.description?.trim() || 'No additional description provided.'}
                      </div>
                    </div>

                    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700 dark:border-blue-900/50 dark:bg-blue-900/20 dark:text-blue-300">
                      New reports are opened automatically. You can only close or cancel your own report from here.
                    </div>

                    {!selectedReportIsTerminal && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          type="button"
                          disabled={updatingReportStatus}
                          onClick={() => void handleUpdateReportStatus(selectedReport, 'closed')}
                          className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
                        >
                          {updatingReportStatus ? 'Updating...' : 'Close Report'}
                        </button>
                        <button
                          type="button"
                          disabled={updatingReportStatus}
                          onClick={() => void handleUpdateReportStatus(selectedReport, 'cancelled')}
                          className="px-4 py-2 rounded-lg bg-gray-600 text-white text-sm font-medium hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                        >
                          {updatingReportStatus ? 'Updating...' : 'Cancel Report'}
                        </button>
                      </div>
                    )}

                    {selectedReportIsTerminal && (
                      <div className="mt-4 rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-300">
                        This report is already in a final state. You can still review details, but no further status changes are available.
                      </div>
                    )}
                  </div>
                </div>
              ) : userReports.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No reports submitted yet</p>
                  <button
                    type="button"
                    onClick={() => setReportType('bug')}
                    className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                  >
                    Submit Your First Report
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={reportSearch}
                      onChange={(e) => setReportSearch(e.target.value)}
                      placeholder="Search reports — title, number, status"
                      className="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-purple-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  {filteredReports.length === 0 && (
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                      No reports match “{reportSearch}”
                    </p>
                  )}
                  {filteredReports.map((report) => (
                    <button
                      type="button"
                      key={report.id}
                      onClick={() => { setSelectedReport(report); setError(null); }}
                      className="w-full p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 bg-white dark:bg-gray-800 transition-colors text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {report.item_type === 'bug' ? (
                            <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                              <Bug className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-sm text-gray-900 dark:text-white line-clamp-1">
                                {report.title}
                              </h4>
                              <span className="text-xs text-gray-500 font-mono">{report.item_number}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${getStatusBadgeClass(report.status)}`}>
                                {report.status?.replace('_', ' ')}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${report.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                report.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                                  report.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                                    'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                }`}>
                                {report.priority || 'medium'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>
                              Submitted {new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {formatRelativeTime(report.created_at)}
                            </span>
                          </div>
                          <div className="mt-2 text-xs font-medium text-purple-600 dark:text-purple-400">Click to view details</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : reportType === 'my-tickets' ? (
          <div>
            <div className="relative mb-3 sm:mb-6 px-2">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
                  <Ticket className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">My Tickets</h1>
              </div>
              <button
                type="button"
                onClick={handleBack}
                aria-label="Back"
                title="Back"
                className="absolute left-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={handleCloseModal} className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-2">
              <MyTicketsView
                permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: false, can_admin: hasModuleAccess }}
                onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
                refreshTrigger={refreshTrigger}
                onTicketClick={(ticket) => { setSelectedTicket(ticket as WorkItem); setShowTicketModal(true); }}
                hasModuleAccess={hasModuleAccess}
                inlineHeader
              />
            </div>
          </div>
        ) : reportType === 'queue' ? (
          <div>
            <div className="relative mb-3 sm:mb-6 px-2">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-slate-500 to-slate-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
                  <Inbox className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">Support Queue</h1>
              </div>
              <button
                type="button"
                onClick={handleBack}
                aria-label="Back"
                title="Back"
                className="absolute left-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={handleCloseModal} className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-2">
              {hasModuleAccess ? (
                <SupportQueueView
                  permissions={{ can_view: true, can_create: true, can_edit: true, can_delete: false, can_admin: true }}
                  onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
                  refreshTrigger={refreshTrigger}
                  onTicketClick={(ticket) => { setSelectedTicket(ticket as WorkItem); setShowTicketModal(true); }}
                  inlineHeader
                />
              ) : (
                <div className="text-center py-12">
                  <Inbox className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">The support queue requires Total Support access</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="relative mb-3 sm:mb-6 px-2">
              <div className="text-center">
                <div className={`mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16 ${reportType === 'bug' ? 'bg-gradient-to-br from-red-500 to-red-600' : reportType === 'ticket' ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                  }`}>
                  {reportType === 'bug' ? <Bug className="h-6 w-6 sm:h-8 sm:w-8 text-white" /> : reportType === 'ticket' ? <LifeBuoy className="h-6 w-6 sm:h-8 sm:w-8 text-white" /> : <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-white" />}
                </div>
                <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">{reportType === 'bug' ? 'Report Bug' : reportType === 'ticket' ? 'New Support Ticket' : 'New Feature'}</h1>
              </div>
              <button
                type="button"
                onClick={handleBack}
                aria-label="Back"
                title="Back"
                className="absolute left-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={handleCloseModal} className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>

            {submitSuccess ? (
              <div className="px-2 py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Submitted Successfully!</h3>
                {lastSubmittedRef && (
                  <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg inline-block">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reference Number</p>
                    <p className="text-lg font-mono font-bold text-gray-900 dark:text-white">{lastSubmittedRef}</p>
                  </div>
                )}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Thank you for your {reportType === 'bug' ? 'bug report' : reportType === 'ticket' ? 'support ticket' : 'feature request'}. Our team will review it soon.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-2 space-y-4 flex-1" onPaste={handlePaste}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter a descriptive title"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide details..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 Tip: You can paste screenshots directly (Ctrl+V / Cmd+V)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attachments</label>
                  <div
                    ref={dropZoneRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                  >
                    {uploadingAttachment ? (
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <BrandedLoadingSpinner
                          size="xs"
                          className="flex-shrink-0"
                          primaryColor="#6b7280"
                          showProgressBar={false}
                          showSurface={false}
                        />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Drag & drop files here, or{' '}
                          <button type="button" onClick={() => fileInputRef.current?.click()} className="text-blue-600 hover:text-blue-700 font-medium">
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Screenshots, logs, or any supporting files (max 10MB each)</p>
                      </>
                    )}
                  </div>

                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.txt,.log,.json,.csv" onChange={handleFileSelect} className="hidden" />

                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                          {attachment.isImage ? (
                            <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0">
                              <img src={attachment.url} alt={attachment.originalFilename} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                              <Paperclip className="w-5 h-5 text-gray-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{attachment.originalFilename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                          </div>
                          <button type="button" onClick={() => removeAttachment(attachment.id)} className="p-1 text-gray-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                      Open (set automatically on creation)
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority <span className="text-red-500">*</span></label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as typeof priority)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      {reportType === 'ticket' ? (
                        <option value="urgent">Urgent</option>
                      ) : (
                        <option value="critical">Critical</option>
                      )}
                    </select>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

              </form>
            )}

            {!submitSuccess && (
              <ModalFooter message={!isSubmitting && !title.trim() ? 'Enter a title' : undefined}>
                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full sm:w-auto px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !title.trim()}
                  className={`w-full sm:w-auto justify-center px-6 py-3 rounded-lg font-medium text-white flex items-center gap-2 transition-all ${reportType === 'bug' ? 'bg-red-500 hover:bg-red-600 disabled:opacity-50' : reportType === 'ticket' ? 'bg-blue-500 hover:bg-blue-600 disabled:opacity-50' : 'bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50'} disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? (
                    <>
                      <BrandedLoadingSpinner
                        size="xs"
                        className="flex-shrink-0"
                        primaryColor="#ffffff"
                        showProgressBar={false}
                        showSurface={false}
                      />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <><Send className="w-5 h-5" /><span>Create</span></>
                  )}
                </button>
              </ModalFooter>
            )}
          </>
        )}
      </div>

      <SupportTicketDetailModal
        isOpen={showTicketModal}
        onClose={() => {
          setShowTicketModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        onRefresh={() => setRefreshTrigger((prev) => prev + 1)}
        viewMode="admin"
      />
    </div>
  ) : null;

  return (
    <>
      {renderButton()}
      {mounted && showModal && createPortal(modalContent, document.body)}
    </>
  );
});

SupportModal.displayName = 'SupportModal';

export default SupportModal;
